import {
  ADMIN_BASE_PATH,
  ADMIN_CHANGE_PASSWORD_PATH,
  ADMIN_DASHBOARD_PATH,
  ADMIN_LOGIN_PATH,
  ADMIN_PROFILE_PATH,
  ADMIN_SETUP_2FA_PATH,
} from '@/lib/admin-paths';
import {
  isAdminMustChangePassword,
  isAdminProtectedApiBlocked,
  isAdminTwoFactorSessionBlocked,
  isAdminTwoFactorSetupRequired,
  type User,
} from '@/lib/auth-store';
import {
  ADMIN_DEDICATED_ROUTES,
  ADMIN_TAB_KEYS,
  type AdminTabKey,
  buildAdminTabHref,
  tabFromAdminPath,
} from '@/config/admin-navigation';
import { resolveDashboardForUser } from '@/lib/role-dashboard';
import { getEffectiveRole, isAdminRole } from '@/lib/rbac';

export { ADMIN_DASHBOARD_PATH } from '@/lib/admin-paths';

/** Normalize admin paths for guard comparisons (trailing slash, query stripped). */
export function normalizeAdminPath(pathname: string | null | undefined): string {
  if (!pathname) return '/';
  return pathname.split('?')[0].replace(/\/$/, '') || '/';
}

/** Tabs reachable during mandatory 2FA onboarding (not gated by module permissions). */
export const ADMIN_SETUP_ONLY_TABS: readonly AdminTabKey[] = [
  ADMIN_TAB_KEYS.SETUP_2FA,
  ADMIN_TAB_KEYS.PROFILE,
  ADMIN_TAB_KEYS.ACCESS_CONTROL,
];

export function isAdminSetupOnlyTab(tab: AdminTabKey): boolean {
  return ADMIN_SETUP_ONLY_TABS.includes(tab);
}

export function canAccessAdminTab(
  tab: AdminTabKey,
  user: User | null | undefined,
  permissionAllowedTabs: Set<AdminTabKey>
): boolean {
  if (isAdminTwoFactorSetupRequired(user) && isAdminSetupOnlyTab(tab)) {
    return true;
  }
  return permissionAllowedTabs.has(tab);
}

export function adminPathsMatch(
  pathname: string | null | undefined,
  target: string | null | undefined
): boolean {
  return normalizeAdminPath(pathname) === normalizeAdminPath(target);
}

/** Paths reachable while mandatory password change is pending. */
export const ADMIN_PASSWORD_CHANGE_EXEMPT_PATHS = [
  ADMIN_CHANGE_PASSWORD_PATH,
  '/admin/change-password',
] as const;

export function isAdminPasswordChangeExemptPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const normalized = normalizeAdminPath(pathname);
  return ADMIN_PASSWORD_CHANGE_EXEMPT_PATHS.some(
    (path) => normalized === path || normalized.startsWith(`${path}/`)
  );
}

/** Paths reachable while 2FA setup is still required. */
export const ADMIN_SETUP_EXEMPT_PATHS = [
  ADMIN_SETUP_2FA_PATH,
  ADMIN_PROFILE_PATH,
  ADMIN_CHANGE_PASSWORD_PATH,
  '/dashboard/admin/access-control',
  '/admin/access-control',
  '/admin/setup-2fa',
  '/admin/profile',
] as const;

export function isAdminSetupExemptPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const normalized = normalizeAdminPath(pathname);
  return ADMIN_SETUP_EXEMPT_PATHS.some(
    (path) => normalized === path || normalized.startsWith(`${path}/`)
  );
}

export function shouldRedirectAdminFromDashboard(
  pathname: string | null | undefined,
  role: string | null | undefined
): boolean {
  if (!pathname || !role || !isAdminRole(role)) return false;
  return pathname === '/dashboard' || pathname.startsWith('/dashboard/');
}

export function shouldRedirectNonAdminFromAdmin(
  pathname: string | null | undefined,
  role: string | null | undefined
): boolean {
  if (!pathname || !role) return false;
  const isAdminPath =
    pathname.startsWith(ADMIN_BASE_PATH) || pathname.startsWith('/admin');
  if (!isAdminPath) return false;
  return !isAdminRole(role);
}

/**
 * Returns a redirect target when the current admin route is inconsistent with auth state.
 */
export function resolveAdminRouteGuard(
  pathname: string | null | undefined,
  user: User | null | undefined
): string | null {
  if (!pathname) return null;
  const isAdminPath =
    pathname.startsWith(ADMIN_BASE_PATH) || pathname.startsWith('/admin');
  if (!isAdminPath) return null;

  const role = getEffectiveRole(user);
  if (!isAdminRole(role)) {
    return ADMIN_LOGIN_PATH;
  }

  if (isAdminTwoFactorSessionBlocked(user) && !isAdminTwoFactorSetupRequired(user)) {
    return '/login?admin=1&reason=two_factor';
  }

  if (isAdminTwoFactorSetupRequired(user)) {
    if (!isAdminSetupExemptPath(pathname)) {
      return ADMIN_SETUP_2FA_PATH;
    }
    return null;
  }

  if (isAdminMustChangePassword(user)) {
    if (!isAdminPasswordChangeExemptPath(pathname)) {
      return ADMIN_CHANGE_PASSWORD_PATH;
    }
    return null;
  }

  if (!isAdminProtectedApiBlocked(user) && adminPathsMatch(pathname, ADMIN_SETUP_2FA_PATH)) {
    return resolveDashboardForUser(user);
  }

  return null;
}

function normalizeLegacyAdminPath(value: string): string {
  if (value === '/admin' || value === '/admin/dashboard') return ADMIN_DASHBOARD_PATH;
  if (value.startsWith('/admin/')) {
    return `${ADMIN_BASE_PATH}${value.slice('/admin'.length)}`;
  }
  return value;
}

/** Longest dedicated admin paths first — used so parent routes (e.g. /davs) do not stay active on children (/davs/drafts). */
const DEDICATED_ADMIN_PATHS = Object.values(ADMIN_DEDICATED_ROUTES)
  .map((value) => value.split('?')[0].replace(/\/$/, '') || '/')
  .filter((value, index, all) => all.indexOf(value) === index)
  .sort((a, b) => b.length - a.length);

function bestDedicatedAdminMatch(currentPath: string): string | null {
  for (const path of DEDICATED_ADMIN_PATHS) {
    if (currentPath === path || currentPath.startsWith(`${path}/`)) {
      return path;
    }
  }
  return null;
}

export function adminNavHrefIsActive(pathname: string | null | undefined, href: string): boolean {
  if (!pathname) return false;

  const normalize = (value: string) => value.replace(/\/$/, '') || '/';
  const current = normalize(normalizeLegacyAdminPath(pathname.split('?')[0]));
  const target = normalize(normalizeLegacyAdminPath(href.split('?')[0]));

  if (target === ADMIN_BASE_PATH || target === ADMIN_DASHBOARD_PATH) {
    // If the current route is a dedicated admin page (e.g. /dashboard/admin/davs/*),
    // then the Dashboard overview should NOT be highlighted.
    if (bestDedicatedAdminMatch(current)) {
      return false;
    }
    return current === ADMIN_BASE_PATH || current === ADMIN_DASHBOARD_PATH;
  }

  // Prefer the longest dedicated route match so siblings under /davs do not all highlight.
  const bestDedicated = bestDedicatedAdminMatch(current);
  if (bestDedicated && DEDICATED_ADMIN_PATHS.includes(target)) {
    return target === bestDedicated;
  }

  if (target.startsWith(ADMIN_BASE_PATH)) {
    return current === target || current.startsWith(`${target}/`);
  }

  if (href.includes('?')) {
    return false;
  }

  return current === target || (target !== '/dashboard' && current.startsWith(`${target}/`));
}

export function adminTabHrefFromPathname(pathname: string | null | undefined): string {
  if (!pathname?.startsWith(ADMIN_BASE_PATH)) return ADMIN_DASHBOARD_PATH;
  const tab = tabFromAdminPath(pathname);
  return buildAdminTabHref(tab);
}
